import React from 'react';

import Chip from '@material-ui/core/Chip';
import green from '@material-ui/core/colors/green';
import grey from '@material-ui/core/colors/grey';
import red from '@material-ui/core/colors/red';
import Popover from '@material-ui/core/Popover';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core/styles';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import Typography from '@material-ui/core/Typography';

import { AUTOMATED_LABEL_KEY_DESCRIPTIONS } from '../util/automated-label-keys';

const CHIP_STYLE = (backgroundColor: string) => ({
  color: 'white',
  margin: '1px',
  height: '25px',
  backgroundColor,
});

// Copied from the material ui - wanted to change the emphasize gradient amount
const CHIP_CLICKABLE = (backgroundColor: string, theme: Record<string, any>) => ({
  WebkitTapHighlightColor: 'transparent', // Remove grey highlight
  cursor: 'pointer',
  '&:hover, &:focus': {
    backgroundColor: emphasize(backgroundColor, 0.1),
  },
  '&:active': {
    boxShadow: theme.shadows[1],
    backgroundColor: emphasize(backgroundColor, 0.1),
  },
});

const CHIP_STYLES = (name: string, backgroundColor: string, theme: Record<string, any>) => {
  const styles = {
    [name]: CHIP_STYLE(backgroundColor),
    [`${name}Clickable`]: CHIP_CLICKABLE(backgroundColor, theme),
  };

  return styles;
};

const styles = (theme: Theme) =>
  createStyles({
    linkText: {
      display: 'inline',
      verticalAlign: 'middle',
      padding: '4px',
    },
    ...CHIP_STYLES('neutral', grey[500], theme),
    ...CHIP_STYLES('good', green[300], theme),
    ...CHIP_STYLES('bad', red[300], theme),
  });

const getClassFromBool = (classes: any, isPositive: boolean | null) => {
  if (isPositive === null) {
    return classes.neutral;
  }
  if (isPositive === true) {
    return classes.good;
  }
  if (isPositive === false) {
    return classes.bad;
  }

  throw new Error('isPositive should be either null, true, or false');
};

const getClickableClassFromBool = (classes: any, isPositive: boolean | null) => {
  if (isPositive === null) {
    return classes.neutralClickable;
  }
  if (isPositive === true) {
    return classes.goodClickable;
  }
  if (isPositive === false) {
    return classes.badClickable;
  }

  throw new Error('isPositive should be either null, true, or false');
};

type InputProps = {
  label: AutomatedLabel;
};

type Props = InputProps & WithStyles;

type State = {
  chipEl: Element | null;
  description: string | null;
};

class JobApplicantLabelComponent extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      chipEl: null,
      description: null,
    };
  }

  showAppLink = (label: AutomatedLabel) => (event: any) => {
    if (!label.automatedLabelKey) {
      return;
    }

    this.setState({
      chipEl: event.target,
      description: AUTOMATED_LABEL_KEY_DESCRIPTIONS[label.automatedLabelKey],
    });
  };

  hideAppLink = () => {
    this.setState({
      description: null,
      chipEl: null,
    });
  };

  render() {
    const { classes, label } = this.props;
    return (
      <React.Fragment key={label.displayName}>
        <Chip
          label={label.displayName}
          className={getClassFromBool(classes, label.isPositive)}
          onClick={this.showAppLink(label)}
          classes={{
            clickable: getClickableClassFromBool(classes, label.isPositive),
          }}
        />
        <Popover
          open={this.state.description !== null}
          anchorEl={this.state.chipEl}
          onClose={this.hideAppLink}
          marginThreshold={32}
          PaperProps={{
            style: {
              maxWidth: '450px',
              padding: '8px',
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: -20,
            horizontal: 'center',
          }}
        >
          <Typography variant="body1" className={classes.linkText}>
            {this.state.description}
          </Typography>
        </Popover>
      </React.Fragment>
    );
  }
}

export const JobApplicantLabel = withStyles(styles)(JobApplicantLabelComponent);
